import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable, throwError } from 'rxjs';
import { environment } from '@env/environment';
import {
  CommerceOptionsFormDataModel,
  ExternalPostData,
  FindPremiumAdReqPayload,
  IBalance,
  OrderInvoiceModel,
  OrderModel,
  PaidTransaction,
  IPremiumAdPricesReqBody,
  IPremiumAdPricesResBody,
  PurchaseUrlWithAmountWithCurrencyReqPayload
} from '@models/commerce-options-form-data.model';
import { PremiumAdModel } from '@models/premium-ad.model';
import { DOCUMENT } from '@angular/common';
import { catchError } from 'rxjs/operators';
import { PremiumAdPeriod } from '@models/profile.model';

@Injectable({
  providedIn: 'root'
})
export class CommerceService {
  #httpClient = inject(HttpClient);
  #baseUrl = `${environment.apiUrl}/commerce`;

  document = inject(DOCUMENT);

  getCommerceOptions(): Observable<CommerceOptionsFormDataModel> {
    const url = `${this.#baseUrl}/options`;

    return this.#httpClient
      .get<CommerceOptionsFormDataModel>(url)
      .pipe(map((json: any) => CommerceOptionsFormDataModel.fromJson(json)));
  }

  findPremiumAdList({
    accountId,
    adIds,
    status
  }: FindPremiumAdReqPayload): Observable<PremiumAdModel[]> {
    const url = `${this.#baseUrl}/premium-ad/_bulk`;
    const body = {
      account_id: accountId,
      ad_ids: adIds,
      status: status
    };

    return this.#httpClient
      .post<PremiumAdModel[]>(url, body)
      .pipe(map((list) => list.map((item) => PremiumAdModel.fromJson(item))));
  }

  getPremiumAdById(id: number): Observable<PremiumAdModel> {
    const url = `${this.#baseUrl}/premium-ad/${id}`;

    return this.#httpClient
      .get<PremiumAdModel>(url)
      .pipe(map((json: any) => PremiumAdModel.fromJson(json)));
  }

  getPremiumAdsByAdId(
    adId: string,
    accountId: number
  ): Observable<PremiumAdPeriod[]> {
    const url = `${this.#baseUrl}/premium-ad/by-ad-id/${adId}`;

    return this.#httpClient
      .get<PremiumAdPeriod[]>(url, { params: { account_id: accountId } })
      .pipe(
        map((data: any) => data.map((json) => PremiumAdModel.fromJson(json)))
      );
  }

  generateBitCoinUrl(): Observable<{ payment_url: string }> {
    const url = `${this.#baseUrl}/bitcoin/payment-url`;
    return this.#httpClient.get<{ payment_url: string }>(url);
  }

  getRecommendedEmerchantpayUrl(
    accountId: number
  ): Observable<{ url: string }> {
    const url = `${this.#baseUrl}/emerchantpay/payment-url`;

    const params = new HttpParams().set('account_id', accountId.toString());
    return this.#httpClient.get<{ url: string }>(url, { params });
  }

  getPurchaseUrlWithAmountWithCurrency(
    payload: PurchaseUrlWithAmountWithCurrencyReqPayload
  ): Observable<{ payment_url: string }> {
    const url = `${this.#baseUrl}${payload.paymentUrl}`;
    let params = new HttpParams().set(
      'account_id',
      payload.accountId.toString()
    );

    if (payload.amount) {
      params = params.set('amount', payload.amount);
    }

    if (payload.currency) {
      params = params.set('currency', payload.currency);
    }

    if (payload.bitcoinCredit) {
      params = params.set('btc_credit', true);
    }

    return this.#httpClient.get<{ payment_url: string }>(url, { params });
  }

  postToExternalSite(externalPostData: ExternalPostData): void {
    const form = this.document.createElement('form');
    form.setAttribute('method', 'post');
    form.setAttribute('action', externalPostData.actionUrl);

    const attrs = Object.assign({}, externalPostData.attrs);

    Object.keys(attrs).forEach((key) => {
      form.appendChild(this.createHiddenInputElement(key, attrs[key]));
    });

    this.document.body.appendChild(form);
    form.submit();
  }

  getCountOfGeneratedPayments(
    accountId: number
  ): Observable<{ count: number }> {
    const url = `${this.#baseUrl}/coinfly`;

    const params = new HttpParams().set('account_id', accountId.toString());

    return this.#httpClient.get<{ count: number }>(url, {
      params
    });
  }

  getListOfGeneratedPayments(
    accountId: number
  ): Observable<Array<PaidTransaction>> {
    const url = `${this.#baseUrl}/coinfly`;

    return this.#httpClient
      .post(url, {
        account_id: accountId
      })
      .pipe(map((json: { payments: Array<PaidTransaction> }) => json.payments));
  }

  removeGeneratedPayments(accountId: number): Observable<{}> {
    const url = `${this.#baseUrl}/coinfly/_bulk`;

    const params = new HttpParams().set('account_id', accountId.toString());

    return this.#httpClient.delete<{}>(url, {
      params
    });
  }

  getBalance(accountId: number): Observable<IBalance> {
    const url = `${this.#baseUrl}/${accountId}/balance`;

    return this.#httpClient.get<IBalance>(url);
  }

  getOrderByRef(refId: number): Observable<OrderModel> {
    const url = `${this.#baseUrl}/order/ref/${refId}`;

    return this.#httpClient.get<OrderModel>(url).pipe(
      map((order) => OrderModel.fromJson(order)),
      catchError((err) => throwError(err))
    );
  }

  getOrderInvoiceDataByOrderId(orderId: number): Observable<OrderInvoiceModel> {
    const url = `${this.#baseUrl}/order/${orderId}/invoice`;

    return this.#httpClient.get<OrderInvoiceModel>(url).pipe(
      map((order) => OrderInvoiceModel.fromJson(order)),
      catchError((err) => throwError(err))
    );
  }

  getPremiumAdPrices({
    account_id,
    ad_id
  }: IPremiumAdPricesReqBody): Observable<IPremiumAdPricesResBody> {
    const url = environment.apiUrl + '/commerce/premium-ad/prices';
    const params = new HttpParams()
      .set('account_id', account_id.toString())
      .set('ad_id', ad_id.toString());

    return this.#httpClient
      .get<IPremiumAdPricesResBody>(url, {
        params: params
      })
      .pipe(map((x) => ({ ...x, identifier: ad_id })));
  }

  private createHiddenInputElement(
    name: string,
    value: string
  ): HTMLInputElement {
    const hiddenField = this.document.createElement('input');
    hiddenField.setAttribute('name', name);
    hiddenField.setAttribute('value', value);
    hiddenField.setAttribute('type', 'hidden');
    return hiddenField;
  }
}
